// V2 Login Page - Минимальный UI, полный функционал
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  TextField,
  Button,
  Typography,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Alert,
  Tabs,
  Tab,
  Divider,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { checkUser, validateStoredToken } from './store/auth/thunks';
import { setUserToLocal, setLogOut } from './store/auth/reducer';
import { clearAuthStorage, getStoredAuth, storeAuth } from './utils/authStorage';
import ProjectSelector from './components/ProjectSelector';
import { TechBackground } from './components/TechBackground';
import PublicLocaleToggle from './components/PublicLocaleToggle';
import { usePublicUiLocale } from './contexts/PublicUiLocaleContext';
import { controller } from './api/controller';
import {
  ENTERPRISE_MODE,
  isEnterpriseModeEnabledFromConfig,
} from './constants/featureFlags';
import { resolveProjectAdminLandingPath } from './utils/projectAdminLanding';

function NewLoginPageV2() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState(null);
  const [hasValidatedToken, setHasValidatedToken] = useState(false);
  const [projectSlug, setProjectSlug] = useState(localStorage.getItem('lastProjectSlug') || '');
  const [projectName, setProjectName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerUsername, setRegisterUsername] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [registerPasswordRepeat, setRegisterPasswordRepeat] = useState('');
  const [registerMessage, setRegisterMessage] = useState(null);
  const [registerError, setRegisterError] = useState(null);
  const [isRegistering, setIsRegistering] = useState(false);
  const [pendingVerificationLogin, setPendingVerificationLogin] = useState('');
  const [resendStatus, setResendStatus] = useState(null);
  const [verifyBanner, setVerifyBanner] = useState(null);
  const [bindEmail, setBindEmail] = useState('');
  const [bindEmailStatus, setBindEmailStatus] = useState(null);
  const [isBindingEmail, setIsBindingEmail] = useState(false);
  const [bindTargetLabel, setBindTargetLabel] = useState('');
  const [publicRegistrationEnabled, setPublicRegistrationEnabled] = useState(
    !ENTERPRISE_MODE,
  );
  const bindEmailBlockRef = useRef(null);
  const bindEmailInputRef = useRef(null);
  const { lang, setLang, login: t } = usePublicUiLocale();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  
  const authState = useSelector((state) => state.authSlice || {
    result: false,
    role: null,
    userName: null,
    isLoading: false,
  });

  // Проверка сохраненного токена
  useEffect(() => {
    if (location.pathname === '/v2/login' && !hasValidatedToken) {
      const storedAuth = getStoredAuth();
      const storedUser = storedAuth?.user || {};
      const storedToken = storedAuth?.token || null;
      
      if (storedUser.result && storedUser.userName && storedToken && !authState.result) {
        setHasValidatedToken(true);
        dispatch(validateStoredToken()).catch(() => {
          clearAuthStorage();
          setHasValidatedToken(false);
        });
      } else if (!storedToken) {
        setHasValidatedToken(true);
      }
    }
  }, [dispatch, location.pathname, authState.result, hasValidatedToken]);

  useEffect(() => {
    if (location.pathname !== '/v2/login') return;
    if (!authState.result || authState.isLoading) return;
    const stored = getStoredAuth()?.user || {};
    const resolvedProjectSlug = String(
      authState.projectSlug || stored.projectSlug || projectSlug || '',
    ).trim();
    if (authState.role === 'superadmin') {
      navigate('/v2/superadmin', { replace: true });
      return;
    }
    if (authState.role === 'support') {
      navigate('/v2/support', { replace: true });
      return;
    }
    if (!resolvedProjectSlug) return;
    if (authState.role === 'admin') {
      const token = getStoredAuth()?.token || localStorage.getItem('token') || sessionStorage.getItem('token');
      resolveProjectAdminLandingPath(resolvedProjectSlug, {
        role: 'admin',
        token,
      }).then((path) => navigate(path, { replace: true }));
      return;
    }
    navigate(`/p/${resolvedProjectSlug}/groups`, { replace: true });
  }, [
    location.pathname,
    authState.result,
    authState.isLoading,
    authState.role,
    authState.projectSlug,
    projectSlug,
    navigate,
  ]);

  const handleLogout = useCallback(() => {
    dispatch(setLogOut());
    setError(null);
    setHasValidatedToken(false);
  }, [dispatch]);

  useEffect(() => {
    let isMounted = true;
    controller
      .getPublicAuthConfig()
      .then((cfg) => {
        if (!isMounted) return;
        setPublicRegistrationEnabled(!isEnterpriseModeEnabledFromConfig(cfg));
      })
      .catch(() => {
        if (!isMounted) return;
        setPublicRegistrationEnabled(!ENTERPRISE_MODE);
      });
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (publicRegistrationEnabled) return;
    if (authMode === 'register') {
      setAuthMode('login');
    }
  }, [publicRegistrationEnabled, authMode]);

  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    if (params.get('mode') === 'register') {
      setAuthMode('register');
    }
    const flag = params.get('emailVerified');
    const bindEmailFlag = params.get('bindEmail');
    const bindFor = params.get('for') || '';

    if (bindEmailFlag === '1') {
      setBindTargetLabel(bindFor);
      setTimeout(() => {
        bindEmailBlockRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
        bindEmailInputRef.current?.focus();
      }, 50);
    }

    if (!flag) return;
    if (flag === '1') {
      const login = params.get('login');
      const label = login ? ` (${login})` : '';
      setVerifyBanner({
        type: 'success',
        text: t.verifySuccess.replace('{{login}}', label),
      });
      if (login) {
        setUsername(login);
      }
    } else {
      const reason = params.get('reason');
      setVerifyBanner({
        type: 'error',
        text: t.verifyFailed.replace('{{reason}}', reason ? `: ${reason}` : '.'),
      });
    }
    navigate('/v2/login', { replace: true });
  }, [location.search, navigate, t.verifyFailed, t.verifySuccess]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setResendStatus(null);
    
    try {
      let slug = projectSlug && projectSlug.trim() ? projectSlug.trim() : '';
      const projectLabel = String(projectName || '').trim();
      if (slug) {
        // Validate saved/selected slug first to avoid "Project not found"
        // when localStorage contains stale lastProjectSlug.
        const checkBySlug = await fetch(`/api/p/${encodeURIComponent(slug)}/check`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });
        const checkBySlugData = await checkBySlug.json().catch(() => ({}));
        if (!checkBySlug.ok || !checkBySlugData?.exists) {
          if (projectLabel) {
            const checkByName = await fetch(`/api/p/_/check?name=${encodeURIComponent(projectLabel)}`, {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
              },
            });
            const checkByNameData = await checkByName.json().catch(() => ({}));
            if (checkByName.ok && checkByNameData?.exists && checkByNameData?.slug) {
              slug = String(checkByNameData.slug).trim();
              setProjectSlug(slug);
              localStorage.setItem('lastProjectSlug', slug);
            } else {
              localStorage.removeItem('lastProjectSlug');
              setError(t.projectNotFound);
              return;
            }
          } else {
            localStorage.removeItem('lastProjectSlug');
            setError(t.projectNotFound);
            return;
          }
        }
      }
      if (!slug && projectName && projectName.trim()) {
        const name = projectName.trim();
        const checkRes = await fetch(`/api/p/_/check?name=${encodeURIComponent(name)}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });
        const checkData = await checkRes.json().catch(() => ({}));
        if (!checkRes.ok || !checkData?.exists || !checkData?.slug) {
          setError(t.projectNotFound);
          return;
        }
        slug = String(checkData.slug).trim();
      }
      if (slug) {
        const response = await fetch(`/api/p/${encodeURIComponent(slug)}/project-auth/login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            username,
            password,
            rememberMe,
            projectSlug: slug,
            lang,
          }),
        });
        if (!response.ok) {
          if (response.status === 401) {
            setError(t.badCredentials);
            return;
          }
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || t.loginFailed);
        }
        const responseData = await response.json();
        if (!responseData?.access_token) {
          setError(t.badCredentials);
          return;
        }
        const u = responseData.user || {};
        const commandPriority = Math.max(
          0,
          Math.min(
            9,
            Math.trunc(
              Number(
                u.commandPriority ?? u.commandpriority ?? u.command_priority ?? 0,
              ) || 0,
            ),
          ),
        );
        const userData = {
          id: u.id,
          result: true,
          role: u.role,
          userName: u.username,
          commandPriority,
          visibleGroups: u.visibleGroups || [],
          email: u.email || null,
          emailVerified: !!u.emailVerified,
          mustVerifyEmail: !!u.mustVerifyEmail,
          blockLoginUntilEmailVerified: !!u.blockLoginUntilEmailVerified,
          projectSlug: slug,
        };
        storeAuth({ user: userData, token: responseData.access_token, rememberMe });
        dispatch(setUserToLocal(userData));
        const role = responseData.user.role;
        if (role === 'superadmin') {
          navigate('/v2/superadmin', { replace: true });
          return;
        }
        if (role === 'admin') {
          const path = await resolveProjectAdminLandingPath(slug, {
            role,
            token: responseData.access_token,
          });
          navigate(path, { replace: true });
          return;
        }
        navigate(`/p/${slug}/groups`, { replace: true });
        return;
      }
      const credentials = { username, password, rememberMe, lang };
      const response = await dispatch(checkUser(credentials)).unwrap();
      
      if (response.user && response.user.id) {
        const u = response.user || {};
        const commandPriority = Math.max(
          0,
          Math.min(
            9,
            Math.trunc(
              Number(
                u.commandPriority ?? u.commandpriority ?? u.command_priority ?? 0,
              ) || 0,
            ),
          ),
        );
        const userData = {
          result: true,
          role: u.role,
          userName: u.username,
          commandPriority,
          visibleGroups: u.visibleGroups || [],
          email: u.email || null,
          emailVerified: !!u.emailVerified,
          mustVerifyEmail: !!u.mustVerifyEmail,
          blockLoginUntilEmailVerified: !!u.blockLoginUntilEmailVerified,
          id: u.id,
        };
        
        storeAuth({ user: userData, token: response.access_token, rememberMe });
        
        // Редирект для суперадмина - на глобальную страницу каталога
        if (response.user.role === 'superadmin') {
          navigate('/v2/superadmin', { replace: true });
          return;
        }
        
        // Редирект для саппорта
        if (response.user.role === 'support') {
          navigate('/v2/support', { replace: true });
          return;
        }
      } else {
        setError(t.badCredentials);
      }
    } catch (err) {
      console.error('Login error:', err);
      clearAuthStorage();
      setHasValidatedToken(false);
      const errorCode = err?.code || err?.response?.data?.code;
      if (errorCode === 'EMAIL_NOT_VERIFIED') {
        setPendingVerificationLogin(username);
      }
      const raw =
        err?.message || err?.response?.data?.message || t.badCredentials;
      if (
        err?.response?.status === 401 ||
        raw === 'Невірний логін або пароль' ||
        raw === 'Invalid username or password' ||
        raw === 'Invalid credentials'
      ) {
        setError(t.badCredentials);
        return;
      }
      const errorMessage =
        raw === 'Проект не найден' || raw === 'Project not found' ? t.projectNotFound : raw;
      setError(errorMessage);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setRegisterError(null);
    setRegisterMessage(null);
    setResendStatus(null);
    if (registerPassword !== registerPasswordRepeat) {
      setRegisterError(t.passwordMismatch);
      return;
    }
    setIsRegistering(true);
    try {
      const result = await controller.registerSuperAdmin({
        email: registerEmail,
        password: registerPassword,
        username: registerUsername || undefined,
        lang,
      });
      if (result?.ok) {
        setRegisterMessage(t.registrationCreated);
        setPendingVerificationLogin(registerEmail.trim());
        setAuthMode('login');
        setRegisterPassword('');
        setRegisterPasswordRepeat('');
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message || err?.message || t.registrationFailed;
      setRegisterError(msg);
    } finally {
      setIsRegistering(false);
    }
  };

  const handleResendVerification = async () => {
    try {
      const loginOrEmail = pendingVerificationLogin || username;
      if (!loginOrEmail) return;
      const result = await controller.resendVerificationEmail({
        loginOrEmail,
        lang,
      });
      if (result?.alreadyVerified) {
        setResendStatus(t.resendSent);
        return;
      }
      setResendStatus(t.resendSent);
    } catch (err) {
      const msg =
        err?.response?.data?.message || err?.message || t.resendFailed;
      setResendStatus(msg);
    }
  };

  const handleBindEmail = async () => {
    if (!bindEmail.trim()) return;
    setBindEmailStatus(null);
    setIsBindingEmail(true);
    try {
      await controller.bindEmail({ email: bindEmail.trim(), lang });
      setBindEmailStatus(t.bindEmailSent);
    } catch (err) {
      const msg =
        err?.response?.data?.message || err?.message || t.bindEmailFailed;
      setBindEmailStatus(msg);
    } finally {
      setIsBindingEmail(false);
    }
  };

  const isAuthedOnV2Login =
    authState.result && !authState.isLoading && location.pathname === '/v2/login';

  /** Logout у куті лише для глобальної сесії (superadmin/support без projectSlug), не для проєктного входу */
  const storedSessionUser = getStoredAuth()?.user;
  const isGlobalV2Session =
    isAuthedOnV2Login && !storedSessionUser?.projectSlug;

  const roleLabel =
    authState.role === 'superadmin'
      ? t.roleSuperadmin
      : authState.role === 'support'
        ? t.roleSupport
        : authState.role === 'admin'
          ? t.roleAdmin
          : authState.role === 'user'
            ? t.roleUser
            : String(authState.role || '');

  const sessionText = t.sessionSignedIn
    .replace('{{user}}', authState.userName || '')
    .replace('{{role}}', roleLabel);

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: 2,
        paddingTop: 7,
        backgroundColor: '#0a1a2f',
        overflow: 'hidden',
      }}
    >
      <TechBackground isLightTheme={false} />

      <Box
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          zIndex: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
          justifyContent: 'flex-end',
        }}
      >
        {isGlobalV2Session && (
          <Button
            variant="outlined"
            size="small"
            onClick={handleLogout}
            sx={{
              color: '#fff',
              borderColor: 'rgba(255,255,255,0.45)',
              textTransform: 'none',
              '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.08)' },
            }}
          >
            {t.logout}
          </Button>
        )}
        <PublicLocaleToggle
          lang={lang}
          onChange={setLang}
          labelUk={t.languageLabelUk}
          labelEn={t.languageLabelEn}
        />
      </Box>

      <Typography
        variant="h5"
        sx={{ color: '#fff', mb: 2, position: 'relative', zIndex: 1, alignSelf: 'center' }}
      >
        {t.title}
      </Typography>

      {verifyBanner && (
        <Alert
          severity={verifyBanner.type}
          sx={{
            width: '100%',
            maxWidth: 400,
            mb: 2,
            position: 'relative',
            zIndex: 1,
          }}
        >
          {verifyBanner.text}
        </Alert>
      )}

      {isGlobalV2Session && (
        <Alert
          severity="info"
          sx={{
            width: '100%',
            maxWidth: 400,
            mb: 2,
            position: 'relative',
            zIndex: 1,
            bgcolor: 'rgba(25, 118, 210, 0.12)',
            color: '#e3f2fd',
            border: '1px solid rgba(144, 202, 249, 0.35)',
          }}
        >
          <Typography variant="body2">{sessionText}</Typography>
        </Alert>
      )}

      {isGlobalV2Session &&
        authState.mustVerifyEmail &&
        publicRegistrationEnabled &&
        !authState.emailVerified && (
          <Alert
            severity="warning"
            sx={{
              width: '100%',
              maxWidth: 400,
              mb: 2,
              position: 'relative',
              zIndex: 1,
            }}
          >
            {t.verifyRequiredSoft}
            <Button
              size="small"
              sx={{ ml: 1 }}
              onClick={handleResendVerification}
            >
              {t.resendVerification}
            </Button>
          </Alert>
        )}

      {isGlobalV2Session &&
        authState.role === 'superadmin' &&
        !authState.email && (
          <Box
            ref={bindEmailBlockRef}
            sx={{
              width: '100%',
              maxWidth: 400,
              mb: 2,
              position: 'relative',
              zIndex: 1,
            }}
          >
            <Alert severity="warning" sx={{ mb: 1 }}>
              {t.bindEmailRequired}
              <Button
                size="small"
                sx={{ ml: 1 }}
                onClick={() => {
                  bindEmailInputRef.current?.focus();
                }}
              >
                {t.bindEmailAction}
              </Button>
            </Alert>
            {bindTargetLabel ? (
              <Typography sx={{ mb: 1, color: '#90caf9' }}>
                {`Аккаунт: ${bindTargetLabel}`}
              </Typography>
            ) : null}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                inputRef={bindEmailInputRef}
                fullWidth
                size="small"
                placeholder={t.bindEmailPlaceholder}
                value={bindEmail}
                onChange={(e) => setBindEmail(e.target.value)}
                inputProps={{ style: { color: '#ffffff' } }}
              />
              <Button
                variant="contained"
                onClick={handleBindEmail}
                disabled={isBindingEmail}
              >
                {t.bindEmailAction}
              </Button>
            </Box>
            {bindEmailStatus && (
              <Typography sx={{ mt: 1, color: '#90caf9' }}>
                {bindEmailStatus}
              </Typography>
            )}
          </Box>
        )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          maxWidth: 400,
          backgroundColor: '#1a2332',
          padding: 3,
          borderRadius: 1,
          position: 'relative',
          zIndex: 1,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        {publicRegistrationEnabled ? (
          <>
            <Tabs
              value={authMode}
              onChange={(_, value) => {
                setAuthMode(value);
                setError(null);
                setRegisterError(null);
              }}
              textColor="inherit"
              indicatorColor="secondary"
              sx={{ mb: 2 }}
            >
              <Tab value="login" label={t.tabLogin} />
              <Tab value="register" label={t.tabRegister} />
            </Tabs>
            <Divider sx={{ mb: 2, borderColor: 'rgba(255,255,255,0.12)' }} />
          </>
        ) : null}

        {authMode === 'login' ? (
          <Box component="form" onSubmit={handleSubmit}>
            <ProjectSelector
              value={projectSlug}
              onChange={setProjectSlug}
              onNameChange={setProjectName}
              label={t.projectLabel}
              helperText={t.projectHelper}
              textFieldProps={{
                sx: { mb: 2 },
                inputProps: { style: { color: '#ffffff' } },
                InputLabelProps: { style: { color: '#b0bec5' } },
                FormHelperTextProps: { style: { color: '#90a4ae' } },
              }}
              addIconSx={{ color: '#90caf9' }}
            />
            <TextField
              label={t.username}
              variant="outlined"
              fullWidth
              sx={{ mb: 2 }}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              inputProps={{ style: { color: '#ffffff' } }}
              InputLabelProps={{ style: { color: '#b0bec5' } }}
            />
            <TextField
              label={t.password}
              type={showPassword ? 'text' : 'password'}
              variant="outlined"
              fullWidth
              sx={{ mb: 2 }}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              inputProps={{ style: { color: '#ffffff' } }}
              InputLabelProps={{ style: { color: '#b0bec5' } }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword((v) => !v)} edge="end">
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  sx={{ color: '#b0bec5' }}
                />
              }
              label={t.rememberMe}
              sx={{ color: '#b0bec5', mb: 2 }}
            />
            {error && (
              <Typography color="error" sx={{ mb: 2 }}>
                {error}
              </Typography>
            )}
            {pendingVerificationLogin && publicRegistrationEnabled && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {t.emailNotVerifiedBlocked}
                <Button
                  size="small"
                  sx={{ ml: 1 }}
                  onClick={handleResendVerification}
                >
                  {t.resendVerification}
                </Button>
              </Alert>
            )}
            {resendStatus && (
              <Typography sx={{ mb: 2, color: '#90caf9' }}>
                {resendStatus}
              </Typography>
            )}
            {registerMessage && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {registerMessage}
              </Alert>
            )}
            <Button type="submit" variant="contained" fullWidth sx={{ mt: 1 }}>
              {t.submit}
            </Button>
          </Box>
        ) : (
          <Box component="form" onSubmit={handleRegister}>
            <TextField
              label={t.registerEmail}
              variant="outlined"
              fullWidth
              sx={{ mb: 2 }}
              value={registerEmail}
              onChange={(e) => setRegisterEmail(e.target.value)}
              inputProps={{ style: { color: '#ffffff' } }}
              InputLabelProps={{ style: { color: '#b0bec5' } }}
            />
            <TextField
              label={t.registerUsernameOptional}
              variant="outlined"
              fullWidth
              sx={{ mb: 2 }}
              value={registerUsername}
              onChange={(e) => setRegisterUsername(e.target.value)}
              inputProps={{ style: { color: '#ffffff' } }}
              InputLabelProps={{ style: { color: '#b0bec5' } }}
            />
            <TextField
              label={t.registerPassword}
              type="password"
              variant="outlined"
              fullWidth
              sx={{ mb: 2 }}
              value={registerPassword}
              onChange={(e) => setRegisterPassword(e.target.value)}
              inputProps={{ style: { color: '#ffffff' } }}
              InputLabelProps={{ style: { color: '#b0bec5' } }}
            />
            <TextField
              label={t.registerPasswordRepeat}
              type="password"
              variant="outlined"
              fullWidth
              sx={{ mb: 2 }}
              value={registerPasswordRepeat}
              onChange={(e) => setRegisterPasswordRepeat(e.target.value)}
              inputProps={{ style: { color: '#ffffff' } }}
              InputLabelProps={{ style: { color: '#b0bec5' } }}
            />
            {registerError && (
              <Typography color="error" sx={{ mb: 2 }}>
                {registerError}
              </Typography>
            )}
            <Button type="submit" variant="contained" fullWidth disabled={isRegistering}>
              {isRegistering ? t.registerSubmitting : t.registerSubmit}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default NewLoginPageV2;

